import { useState } from 'react';
import type { Operaciones } from '../api/operaciones';
import type { AltaDosPasos } from '../api/ops/cuenta';
import { CodigoQr } from '../componentes/CodigoQr';
import { CodigoSeis } from '../componentes/CodigoSeis';
import { MENSAJES, mensajeDeError, validarSegundoPaso } from '../logica/acceso';
import { PASOS_ALTA, TEXTOS_PROTEGER, claveEnGrupos } from '../logica/altaCuenta';
import { HojaRoja } from './InvitacionHoja';

/**
 * «Protege tu cuenta» (1.6, `diseno/plataforma/1.1-1.6-1.10-alta-e-inicio-de-sesion.html`, pantallas `offer2fa` y
 * `twofaSetup`) en el alta de un propietario nuevo, entre «Email verificado» y «Tu negocio» (tarea 490): se ofrecen los
 * dos pasos sin obligar (G65). «Activarla ahora» pide el secreto (`startTwoFactorEnrollment`), se escanea el QR (o se
 * copia la clave) y el primer código lo confirma (`confirmTwoFactorEnrollment`), que devuelve los códigos de
 * recuperación: se enseñan una vez con los textos de «Mi cuenta» (1.8, G1027). «Ahora no» sigue al alta del negocio.
 *
 * También tras aceptar una invitación con una cuenta nueva (1.10, `ctx: 'invite'` del dibujo): sin «Paso N de 4» y con
 * «Ya formas parte de [negocio].» delante del texto; después, al panel de ese negocio.
 *
 * Y obligatoria (`force2fa`, G65, G123): Soporte o Superadmin sin los dos pasos. Sin «Ahora no» ni «Paso N de 4»; «Volver»
 * cierra la sesión. Al confirmar, la sesión queda verificada (`confirmTwoFactorEnrollment`) y se sigue al backoffice.
 */
interface Props {
    ops: Operaciones;
    /** Activados o «Ahora no»: a «Tu negocio» (1.5) o, desde la invitación, al panel. */
    onSeguir: () => void;
    /** Desde la invitación (1.10): el negocio al que ya pertenece. Sin él, es el alta del propietario. */
    negocioDeLaInvitacion?: string;
    /** Cuenta de plataforma sin los dos pasos (`force2fa`): activarlos es obligatorio; «Volver» cierra la sesión. */
    obligatoria?: { onSalir: () => void };
}

type Paso = 'ofrecer' | 'activar' | 'codigos';

export function ProtegerCuenta({ ops, onSeguir, negocioDeLaInvitacion, obligatoria }: Props) {
    const paso = negocioDeLaInvitacion === undefined && obligatoria === undefined ? PASOS_ALTA.proteger : undefined;
    const [fase, setFase] = useState<Paso>('ofrecer');
    const [alta, setAlta] = useState<AltaDosPasos | null>(null);
    const [verClave, setVerClave] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [codigos, setCodigos] = useState<string[]>([]);
    const [copiados, setCopiados] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const empezar = () => {
        setOcupado(true);
        setError(null);
        ops.startTwoFactorEnrollment()
            .then((r) => {
                setAlta(r);
                setCodigo('');
                setVerClave(false);
                setFase('activar');
            })
            .catch((e: unknown) => {
                setError(mensajeDeError(e, 'contrasena'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const confirmar = (valor = codigo) => {
        const mal = validarSegundoPaso(false, valor);
        if (mal !== null) {
            setError(mal);
            return;
        }
        setOcupado(true);
        setError(null);
        ops.confirmTwoFactorEnrollment(valor)
            .then((r) => {
                setCodigos(r.recovery_codes);
                setFase('codigos');
            })
            .catch((e: unknown) => {
                setCodigo('');
                setError(mensajeDeError(e, 'dos-pasos'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    switch (fase) {
        case 'ofrecer':
            if (obligatoria !== undefined) {
                return (
                    <HojaRoja
                        giro={18 * 60 + 12}
                        volver={{ texto: 'Volver', accion: obligatoria.onSalir }}
                        baldosa={['shield-alert', 'var(--rv-ink)', '#fff']}
                        titulo={MENSAJES.activaDosPasosTitulo}
                        sub={MENSAJES.soporteSinDosPasos}
                        error={error}
                        ocupado={ocupado}
                        principal={{ texto: TEXTOS_PROTEGER.activar, icono: 'shield-check', ocupado: TEXTOS_PROTEGER.preparando, accion: empezar }}
                    />
                );
            }
            return (
                <HojaRoja
                    giro={9 * 60 + 12}
                    paso={paso}
                    baldosa={['shield-check', 'var(--rv-ink)', '#fff']}
                    titulo={TEXTOS_PROTEGER.titulo}
                    sub={negocioDeLaInvitacion === undefined ? TEXTOS_PROTEGER.sub : TEXTOS_PROTEGER.subInvitacion(negocioDeLaInvitacion)}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: TEXTOS_PROTEGER.activarAhora, icono: 'shield-check', ocupado: TEXTOS_PROTEGER.preparando, accion: empezar }}
                    enlaces={[{ texto: TEXTOS_PROTEGER.ahoraNo, accion: ocupado ? undefined : onSeguir }]}
                />
            );
        case 'activar':
            return (
                <HojaRoja
                    giro={10 * 60 + 12}
                    paso={paso}
                    volver={{ texto: 'Volver', accion: () => { setError(null); setFase('ofrecer'); } }}
                    titulo={TEXTOS_PROTEGER.activaTitulo}
                    sub={TEXTOS_PROTEGER.activaSub}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: TEXTOS_PROTEGER.activar, icono: 'shield-check', ocupado: 'Comprobando…', accion: () => { confirmar(); } }}
                    enlaces={verClave ? [] : [{ antes: TEXTOS_PROTEGER.noPuedes, texto: TEXTOS_PROTEGER.verClave, accion: () => { setVerClave(true); } }]}
                >
                    {alta !== null && (
                        <div className="rv-acceso__qr">
                            <CodigoQr valor={alta.otpauth_uri} etiqueta="Código QR para tu aplicación de autenticación" tamano={168} />
                        </div>
                    )}
                    {verClave && alta !== null && <code className="rv-acceso__clave">{claveEnGrupos(alta.secret)}</code>}
                    <div className="rv-acceso__campo">
                        <CodigoSeis valor={codigo} onChange={setCodigo} error={error !== null} autoFocus onCompleto={confirmar} />
                    </div>
                </HojaRoja>
            );
        case 'codigos':
            return (
                <HojaRoja
                    giro={10 * 60 + 12}
                    paso={paso}
                    baldosa={['shield-check', 'var(--rv-red)', '#fff']}
                    titulo={TEXTOS_PROTEGER.codigosTitulo}
                    sub={TEXTOS_PROTEGER.codigosSub}
                    principal={{ texto: TEXTOS_PROTEGER.guardados, icono: 'arrow-right', ocupado: 'Un momento…', accion: onSeguir }}
                    enlaces={[
                        {
                            texto: copiados ? TEXTOS_PROTEGER.copiados : TEXTOS_PROTEGER.copiar,
                            accion: () => {
                                navigator.clipboard
                                    .writeText(codigos.join('\n'))
                                    .then(() => {
                                        setCopiados(true);
                                    })
                                    .catch(() => undefined);
                            },
                        },
                    ]}
                >
                    <ol className="rv-acceso__codigos" aria-label="Códigos de recuperación">
                        {codigos.map((c) => (
                            <li key={c}>{c}</li>
                        ))}
                    </ol>
                </HojaRoja>
            );
    }
}
